/*
  Iter122k139 · Credius · Tab "Editor Diffide" — Fase 1.
  Visibile solo se pratica.settore === "Recupero crediti".
  L'editor dei modelli globali (4 template) è ulteriormente riservato allo
  Studio Izzo Admin (tenant_id === "studio") — il pulsante appare solo per lui.
*/
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Loader2, Upload, FileSpreadsheet, FileText, Download, Trash2, Send, Mail,
  ChevronDown, ChevronUp, Pencil, ImagePlus, ScrollText, Info, CheckCircle2,
  Package, Euro, Paperclip, X, ExternalLink, Calendar, Handshake, Shield,
  PenTool,
} from "lucide-react";
import { DiffideTemplateEditor } from "./DiffideTemplateEditor";
import { useAuth } from "../context/AuthContext";

const fmtEUR = (v) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);

const STATO_STYLE = {
  bozza: "bg-slate-100 text-slate-700 border-slate-300",
  generata: "bg-sky-100 text-sky-800 border-sky-300",
  inviata: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export function CrediusDiffide({ pratica, isAdmin }) {
  const pid = pratica?.id;
  const { user } = useAuth();
  const isStudioIzzo = user?.role === "admin" && user?.tenant_id === "studio";
  const [data, setData] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tipoImport, setTipoImport] = useState("interna");
  const [openId, setOpenId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [invioModal, setInvioModal] = useState(null);   // { diffida }
  const [pdrModal, setPdrModal] = useState(null);       // { diffida }
  // Iter123k163 · elaborazione massiva Excel (template + preview + genera)
  const [massivaPreview, setMassivaPreview] = useState(null); // { totale, validi, invalidi, righe, tipo }
  const [massivaBusy, setMassivaBusy] = useState(false);
  const massivaFileRef = useRef(null);
  const cartaRef = useRef(null);
  const firmaRef = useRef(null);

  const load = () =>
    Promise.all([
      api.get(`/credius/${pid}/diffide`).then((r) => setData(r.data)),
      api.get(`/credius/${pid}/diffide/dashboard`).then((r) => setDashboard(r.data)),
    ]).catch(() => setData({ diffide: [], can_edit: false, carta_intestata: false }));

  useEffect(() => { if (pid) load(); /* eslint-disable-next-line */ }, [pid]);

  const canEdit = data?.can_edit;

  // Iter123k163 · Scarica template Excel (vuoto o precompilato con debitori attuali)
  const downloadTemplate = async (populated) => {
    setMassivaBusy(true);
    try {
      const r = await api.get(`/credius/${pid}/diffide/excel-template`, {
        params: { populated }, responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `diffide-${populated ? "debitori-attuali" : "template-vuoto"}-${pratica?.numero_pratica || "pratica"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(populated ? "Export debitori attuali scaricato" : "Template vuoto scaricato");
    } catch (e) { toast.error(formatApiError(e) || "Download fallito"); }
    finally { setMassivaBusy(false); }
  };

  // Iter123k163 · Preview import Excel (nessuna diffida creata finché non si conferma)
  const doMassivaPreview = async (file) => {
    if (!file) return;
    setMassivaBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: r } = await api.post(`/credius/${pid}/diffide/excel-preview`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMassivaPreview({ ...r, tipo: tipoImport });
    } catch (e) { toast.error(formatApiError(e) || "Anteprima fallita"); }
    finally { setMassivaBusy(false); if (massivaFileRef.current) massivaFileRef.current.value = ""; }
  };

  // Iter123k163 · Genera N diffide dopo conferma preview
  const doMassivaGenera = async () => {
    if (!massivaPreview) return;
    const righe = (massivaPreview.righe || []).filter((r) => r._valid);
    if (righe.length === 0) { toast.error("Nessuna riga valida da generare"); return; }
    setMassivaBusy(true);
    try {
      // Pulisce righe da campi _row/_warnings/_valid prima di inviare al backend
      const payload = {
        tipo: massivaPreview.tipo,
        righe: righe.map(({ _row, _warnings, _valid, ...rest }) => rest),
      };
      const { data: r } = await api.post(`/credius/${pid}/diffide/excel-genera`, payload);
      toast.success(`Generate ${r.diffide_create} diffide in bozza`);
      setMassivaPreview(null);
      load();
    } catch (e) { toast.error(formatApiError(e) || "Generazione fallita"); }
    finally { setMassivaBusy(false); }
  };

  const uploadCarta = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/credius/${pid}/diffide/carta-intestata`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Carta intestata caricata");
      load();
    } catch (e) { toast.error(formatApiError(e) || "Upload fallito"); }
    finally { setBusy(false); if (cartaRef.current) cartaRef.current.value = ""; }
  };

  const rimuoviCarta = async () => {
    if (!window.confirm("Rimuovere la carta intestata?")) return;
    setBusy(true);
    try {
      await api.delete(`/credius/${pid}/diffide/carta-intestata`);
      toast.success("Carta intestata rimossa");
      load();
    } catch (e) { toast.error(formatApiError(e) || "Rimozione fallita"); }
    finally { setBusy(false); }
  };

  // Iter123k174 · firma avvocato
  const uploadFirma = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/credius/${pid}/diffide/firma`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Firma avvocato caricata");
      load();
    } catch (e) { toast.error(formatApiError(e) || "Upload firma fallito"); }
    finally { setBusy(false); if (firmaRef.current) firmaRef.current.value = ""; }
  };

  const rimuoviFirma = async () => {
    if (!window.confirm("Rimuovere la firma avvocato?")) return;
    setBusy(true);
    try {
      await api.delete(`/credius/${pid}/diffide/firma`);
      toast.success("Firma rimossa");
      load();
    } catch (e) { toast.error(formatApiError(e) || "Rimozione fallita"); }
    finally { setBusy(false); }
  };

  const anteprimaPDF = async (d) => {
    try {
      const r = await api.get(`/credius/${pid}/diffide/${d.id}/pdf`, { responseType: "blob" });
      // Iter123k173 · Blob senza `type` → il browser mobile mostrava il PDF come testo grezzo.
      // Impostiamo esplicitamente `application/pdf` così Chrome Android apre il viewer nativo.
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // Rilascia il blob dopo che la nuova tab l'ha caricato (safe: se aperta già, il browser mantiene il riferimento)
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      load();
    } catch (e) { toast.error(formatApiError(e) || "PDF non generato"); }
  };

  const scaricaDOCX = async (d) => {
    try {
      const r = await api.get(`/credius/${pid}/diffide/${d.id}/docx`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `diffida-${d.debitore?.nome || d.id}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e) || "DOCX non generato"); }
  };

  const uploadWord = async (d, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/credius/${pid}/diffide/${d.id}/upload-word`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Versione Word caricata");
      load();
    } catch (e) { toast.error(formatApiError(e) || "Upload fallito"); }
    finally { setBusy(false); }
  };

  const rimuovi = async (d) => {
    if (!window.confirm(`Rimuovere la diffida per ${d.debitore?.nome}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/credius/${pid}/diffide/${d.id}`);
      toast.success("Diffida rimossa"); load();
    } catch (e) { toast.error(formatApiError(e) || "Rimozione fallita"); }
    finally { setBusy(false); }
  };

  const salvaParams = async (d, patch) => {
    setBusy(true);
    try {
      await api.patch(`/credius/${pid}/diffide/${d.id}`, patch);
      toast.success("Parametri salvati"); load();
    } catch (e) { toast.error(formatApiError(e) || "Salvataggio fallito"); }
    finally { setBusy(false); }
  };

  // Iter122k140 · registra invio / rimuovi invio / upload ricevuta
  const registraInvio = async (d, payload) => {
    setBusy(true);
    try {
      await api.post(`/credius/${pid}/diffide/${d.id}/invio`, payload);
      toast.success("Invio registrato"); load(); setInvioModal(null);
    } catch (e) { toast.error(formatApiError(e) || "Registrazione fallita"); }
    finally { setBusy(false); }
  };
  const annullaInvio = async (d, invioId) => {
    if (!window.confirm("Annullare questa registrazione di invio?")) return;
    setBusy(true);
    try {
      await api.delete(`/credius/${pid}/diffide/${d.id}/invio/${invioId}`);
      toast.success("Invio annullato"); load();
    } catch (e) { toast.error(formatApiError(e) || "Operazione fallita"); }
    finally { setBusy(false); }
  };
  const uploadRicevuta = async (d, tipo, file, note = "") => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file); form.append("tipo", tipo); form.append("note", note);
      await api.post(`/credius/${pid}/diffide/${d.id}/ricevute`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Ricevuta caricata"); load();
    } catch (e) { toast.error(formatApiError(e) || "Upload fallito"); }
    finally { setBusy(false); }
  };
  const scaricaRicevuta = async (d, r) => {
    try {
      const resp = await api.get(`/credius/${pid}/diffide/${d.id}/ricevute/${r.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = r.nome_originale || `ricevuta-${r.tipo}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e) || "Download non riuscito"); }
  };
  const rimuoviRicevuta = async (d, rId) => {
    if (!window.confirm("Rimuovere la ricevuta?")) return;
    setBusy(true);
    try {
      await api.delete(`/credius/${pid}/diffide/${d.id}/ricevute/${rId}`);
      toast.success("Ricevuta rimossa"); load();
    } catch (e) { toast.error(formatApiError(e) || "Operazione fallita"); }
    finally { setBusy(false); }
  };

  if (!data) return (
    <div className="flex justify-center py-8">
      <Loader2 className="w-6 h-6 animate-spin text-red-800" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="credius-diffide">
      {/* Iter122k140 · Dashboard tracking invii */}
      {dashboard && dashboard.totale_diffide > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2"
             data-testid="credius-diffide-dashboard">
          <MetricCard label="Totale" value={dashboard.totale_diffide} testid="diffide-metric-totale" />
          <MetricCard label="Inviate" value={dashboard.per_stato?.inviata || 0}
            highlight="emerald" testid="diffide-metric-inviate" />
          <MetricCard label="Bozze" value={dashboard.per_stato?.bozza || 0}
            highlight="slate" testid="diffide-metric-bozze" />
          <MetricCard label="Invii totali" value={dashboard.invii_totali}
            testid="diffide-metric-invii" />
          <MetricCard label="Costo totale invii" value={fmtEUR(dashboard.costo_totale)}
            highlight="brand" testid="diffide-metric-costo" isText />
        </div>
      )}

      {/* Iter123k174 · Toolbar unificata diffide: Excel + Carta intestata + Firma + Modelli */}
      {canEdit && (
        <div className="rounded-xl border-2 border-brand/30 bg-[#FBEEF3]/40 p-4 space-y-3" data-testid="diffide-toolbar-box">
          <div className="flex items-start gap-2 text-sm text-slate-700">
            <FileSpreadsheet className="w-4 h-4 mt-0.5 text-brand shrink-0" />
            <div>
              <div className="font-bold text-brand-dark">Elaborazione massiva diffide via Excel</div>
              <div className="text-xs text-slate-600 mt-0.5">
                Scarica un template Excel (vuoto o con debitori attuali), compilalo e ricaricalo
                per generare in batch le missive. <b>L'anteprima mostra le righe prima di creare
                qualsiasi diffida.</b> Carica inoltre la <b>carta intestata</b> dello Studio
                (PNG/JPEG o .docx) e la <b>firma</b> dell'avvocato: verranno inserite in
                anteprima PDF e in DOCX.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
            <div className="col-span-2 sm:col-span-1 flex items-center gap-2">
              <Label className="text-xs uppercase font-bold">Tipo</Label>
              <select value={tipoImport} onChange={(e) => setTipoImport(e.target.value)}
                data-testid="diffide-tipo-import"
                className="h-9 flex-1 sm:flex-none border-2 border-slate-300 rounded-md px-2 text-sm bg-white">
                <option value="interna">Interna (rate condominiali)</option>
                <option value="esterna">Esterna (fatture insolute)</option>
              </select>
            </div>
            <Button variant="outline" size="sm" data-testid="massiva-tpl-vuoto" disabled={massivaBusy}
              onClick={() => downloadTemplate(false)}
              className="h-9 border-brand text-brand hover:bg-brand/10 justify-center">
              <Download className="w-4 h-4 mr-1" /> Template vuoto
            </Button>
            <Button variant="outline" size="sm" data-testid="massiva-tpl-popolato" disabled={massivaBusy}
              onClick={() => downloadTemplate(true)}
              className="h-9 border-brand text-brand hover:bg-brand/10 justify-center">
              <Download className="w-4 h-4 mr-1" /> Export debitori attuali
            </Button>
            <label className="col-span-2 cursor-pointer">
              <input ref={massivaFileRef} type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                data-testid="massiva-import-file"
                onChange={(e) => doMassivaPreview(e.target.files?.[0])}
                className="hidden" disabled={massivaBusy} />
              <span className={`inline-flex items-center justify-center gap-1 h-9 w-full px-3 rounded-md border-2 text-sm font-semibold ${massivaBusy ? "opacity-50 cursor-not-allowed" : "border-brand bg-brand text-white hover:bg-brand-dark"}`}>
                {massivaBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica Excel per anteprima
              </span>
            </label>
          </div>

          <div className="border-t border-brand/20 pt-3 grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
            {/* Carta intestata */}
            <label className="cursor-pointer">
              <input ref={cartaRef} type="file"
                accept="image/png,image/jpeg,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                data-testid="diffide-carta-file"
                onChange={(e) => uploadCarta(e.target.files?.[0])}
                className="hidden" disabled={busy} />
              <span className={`inline-flex items-center justify-center gap-1 h-9 w-full px-3 rounded-md border-2 text-sm font-semibold ${data.carta_intestata ? "border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
                title="Carta intestata — ammessi PNG/JPEG o .docx">
                <ImagePlus className="w-4 h-4" />
                {data.carta_intestata
                  ? `Carta intestata ${data.carta_intestata_kind === "docx" ? "DOCX" : "img"} · ok`
                  : "Carta intestata"}
              </span>
            </label>
            {data.carta_intestata && (
              <Button variant="outline" size="sm" onClick={rimuoviCarta} disabled={busy}
                data-testid="diffide-carta-remove"
                className="h-9 border-red-300 text-red-700 hover:bg-red-50 justify-center">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Rimuovi carta
              </Button>
            )}

            {/* Firma avvocato */}
            <label className="cursor-pointer">
              <input ref={firmaRef} type="file"
                accept="image/png,image/jpeg"
                data-testid="diffide-firma-file"
                onChange={(e) => uploadFirma(e.target.files?.[0])}
                className="hidden" disabled={busy} />
              <span className={`inline-flex items-center justify-center gap-1 h-9 w-full px-3 rounded-md border-2 text-sm font-semibold ${data.firma ? "border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
                title="Firma dell'avvocato (PNG/JPEG). Verrà apposta a chiusura di PDF e DOCX.">
                <PenTool className="w-4 h-4" />
                {data.firma ? "Firma avvocato · ok" : "Firma avvocato"}
              </span>
            </label>
            {data.firma && (
              <Button variant="outline" size="sm" onClick={rimuoviFirma} disabled={busy}
                data-testid="diffide-firma-remove"
                className="h-9 border-red-300 text-red-700 hover:bg-red-50 justify-center">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Rimuovi firma
              </Button>
            )}

            {isStudioIzzo && (
              <Button variant="outline" onClick={() => setEditorOpen(true)}
                data-testid="diffide-open-editor"
                className="h-9 col-span-2 sm:ml-auto sm:col-span-1 border-brand text-brand hover:bg-brand/10 justify-center">
                <ScrollText className="w-4 h-4 mr-1" /> Modelli (Studio Izzo)
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Lista diffide */}
      {(data.diffide || []).length === 0 ? (
        <div data-testid="diffide-empty"
          className="rounded-xl border-2 border-dashed border-slate-200 py-10 text-center text-slate-500">
          <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          Nessuna diffida generata. Carica un file Excel di anagrafiche-insoluti per iniziare.
        </div>
      ) : (
        <div className="space-y-2">
          {data.diffide.map((d) => {
            const open = openId === d.id;
            const canale = (d.params?.canale_invio || "PEC");
            const hasPEC = !!(d.debitore?.pec);
            return (
              <div key={d.id} className="rounded-xl border-2 border-slate-200 bg-white"
                   data-testid={`diffida-row-${d.id}`}>
                <div className="p-3 flex flex-wrap items-center gap-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATO_STYLE[d.stato] || STATO_STYLE.bozza}`}>
                    {d.stato}
                  </span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${d.tipo === "interna" ? "bg-indigo-50 border-indigo-200 text-indigo-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                    {d.tipo === "interna" ? "Interna" : "Esterna"}
                  </span>
                  <span className="font-bold text-slate-900 truncate">{d.debitore?.nome || "—"}</span>
                  {d.debitore?.pec && <span className="text-[11px] text-emerald-700 inline-flex items-center gap-0.5"><Mail className="w-3 h-3" />PEC</span>}
                  <span className="ml-auto text-sm">
                    <span className="text-slate-500">Insoluto:</span> <b>{fmtEUR(d.debitore?.totale_insoluto)}</b>
                  </span>
                  <button type="button" onClick={() => setOpenId(open ? null : d.id)}
                    className="p-1 rounded hover:bg-slate-100 text-slate-500">
                    {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>

                {open && (
                  <div className="p-3 pt-0 border-t border-slate-100 space-y-3">
                    <div className="grid sm:grid-cols-3 gap-2 text-xs text-slate-600">
                      <div><b>CF:</b> {d.debitore?.codice_fiscale || "—"}</div>
                      <div><b>Indirizzo:</b> {d.debitore?.via} {d.debitore?.civico}</div>
                      <div><b>Città:</b> {d.debitore?.cap} {d.debitore?.citta} ({d.debitore?.provincia})</div>
                      <div><b>Email:</b> {d.debitore?.email || "—"}</div>
                      <div><b>PEC:</b> {d.debitore?.pec || "—"}</div>
                      <div><b>Tel:</b> {d.debitore?.telefono || "—"}</div>
                    </div>
                    {canEdit && (
                      <ParamsEditor d={d} onSave={(patch) => salvaParams(d, patch)} disabled={busy} />
                    )}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button size="sm" onClick={() => anteprimaPDF(d)}
                        data-testid={`diffida-pdf-${d.id}`}
                        className="bg-brand hover:bg-brand-dark">
                        <FileText className="w-4 h-4 mr-1" /> Anteprima PDF
                      </Button>
                      {canEdit && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => scaricaDOCX(d)}
                            data-testid={`diffida-docx-${d.id}`}
                            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                            <Download className="w-4 h-4 mr-1" /> Scarica DOCX
                          </Button>
                          <label className="cursor-pointer">
                            <input type="file" accept=".docx,.doc" className="hidden"
                              data-testid={`diffida-upload-word-${d.id}`}
                              onChange={(e) => uploadWord(d, e.target.files?.[0])} />
                            <span className="inline-flex items-center gap-1 h-9 px-3 rounded-md border-2 border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold">
                              <Upload className="w-4 h-4" /> Ricarica Word modificato
                            </span>
                          </label>
                          <span className="text-[11px] text-slate-500 self-center ml-2">
                            {hasPEC ? "Invio suggerito: PEC" : "Invio suggerito: Raccomandata A/R"}
                          </span>
                          {d.docx_override_file_id && (
                            <span className="text-[11px] text-emerald-700 self-center inline-flex items-center gap-0.5">
                              <CheckCircle2 className="w-3 h-3" /> Versione Word personalizzata caricata
                            </span>
                          )}
                          <Button size="sm" onClick={() => setPdrModal({ diffida: d })}
                            data-testid={`diffida-pdr-open-${d.id}`}
                            className="bg-indigo-700 hover:bg-indigo-800">
                            <Handshake className="w-4 h-4 mr-1" />
                            {d.pdr ? "Modifica PDR" : "Nuovo PDR"}
                            {d.pdr?.stato === "firmato" && <Shield className="w-3 h-3 ml-1 text-emerald-300" />}
                          </Button>
                          <Button size="sm" onClick={() => setInvioModal({ diffida: d })}
                            data-testid={`diffida-registra-invio-${d.id}`}
                            className="bg-emerald-700 hover:bg-emerald-800">
                            <Send className="w-4 h-4 mr-1" /> Registra invio
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => rimuovi(d)}
                            data-testid={`diffida-del-${d.id}`}
                            className="ml-auto border-red-300 text-red-700 hover:bg-red-50">
                            <Trash2 className="w-4 h-4 mr-1" /> Rimuovi
                          </Button>
                        </>
                      )}
                    </div>

                    {/* Storico invii */}
                    {(d.invii_history || []).length > 0 && (
                      <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 space-y-1">
                        <div className="text-[11px] font-bold uppercase text-slate-500 flex items-center gap-1">
                          <Send className="w-3 h-3" /> Invii registrati ({d.invii_history.length})
                        </div>
                        {d.invii_history.map((iv) => (
                          <div key={iv.id} className="flex items-center gap-2 text-xs">
                            <span className="font-mono text-slate-500">{iv.data}</span>
                            <span className="font-semibold">{iv.mezzo}</span>
                            {iv.costo > 0 && (
                              <span className="text-emerald-700 inline-flex items-center gap-0.5">
                                <Euro className="w-3 h-3" />{fmtEUR(iv.costo)}
                              </span>
                            )}
                            {(iv.allegati_ids || []).length > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-slate-500">
                                <Paperclip className="w-3 h-3" />{iv.allegati_ids.length}
                              </span>
                            )}
                            {iv.note && <span className="text-slate-500 truncate flex-1">— {iv.note}</span>}
                            {canEdit && (
                              <button type="button" onClick={() => annullaInvio(d, iv.id)}
                                data-testid={`diffida-invio-del-${iv.id}`}
                                className="ml-auto text-red-600 hover:text-red-800" title="Annulla invio">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Ricevute */}
                    {canEdit && (
                      <RicevuteBox d={d}
                        onUpload={(tipo, file, note) => uploadRicevuta(d, tipo, file, note)}
                        onDownload={(r) => scaricaRicevuta(d, r)}
                        onDelete={(rId) => rimuoviRicevuta(d, rId)}
                        disabled={busy} />
                    )}

                    {/* PDR summary */}
                    {d.pdr && <PdrSummaryBox d={d} pid={pid} onReload={load} disabled={busy} />}
                    {/* Info integrazione futura */}
                    <div className="text-[10px] text-slate-400 italic">
                      Postalizzazione online (es. Posta Power) — integrazione in arrivo.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {invioModal && (
        <RegistraInvioModal
          diffida={invioModal.diffida}
          documenti={pratica?.documenti || []}
          onClose={() => setInvioModal(null)}
          onSubmit={(payload) => registraInvio(invioModal.diffida, payload)} />
      )}

      {pdrModal && (
        <PdrWizardModal
          pid={pid} diffida={pdrModal.diffida}
          onClose={() => setPdrModal(null)}
          onSaved={() => { setPdrModal(null); load(); }} />
      )}

      {editorOpen && (
        <DiffideTemplateEditor onClose={() => setEditorOpen(false)} />
      )}

      {/* Iter123k163 · Modal anteprima elaborazione massiva */}
      {massivaPreview && (
        <MassivaPreviewModal
          preview={massivaPreview}
          setPreview={setMassivaPreview}
          busy={massivaBusy}
          onConfirm={doMassivaGenera}
          onClose={() => setMassivaPreview(null)}
        />
      )}
    </div>
  );
}


// Iter123k163 · Modal anteprima elaborazione massiva Excel diffide
function MassivaPreviewModal({ preview, setPreview, busy, onConfirm, onClose }) {
  const { totale, validi, invalidi, righe, tipo } = preview;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" data-testid="massiva-preview-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="sticky top-0 bg-white border-b-2 border-brand/20 px-4 py-3 flex items-center justify-between z-10">
          <div>
            <div className="font-bold text-lg text-slate-800 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-brand" /> Anteprima elaborazione massiva
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Nessuna diffida creata finché non confermi.</div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="massiva-close"><X className="w-5 h-5" /></Button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 border border-slate-300 text-slate-700 font-bold">
              Totale righe: {totale}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-300 text-emerald-800 font-bold">
              <CheckCircle2 className="w-3.5 h-3.5" /> Valide: {validi}
            </span>
            {invalidi > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-50 border border-red-300 text-red-800 font-bold">
                Da correggere: {invalidi}
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Label className="text-xs uppercase">Modello</Label>
              <select value={tipo} onChange={(e) => setPreview((p) => ({ ...p, tipo: e.target.value }))}
                data-testid="massiva-tipo-scelta"
                className="h-9 border-2 border-slate-300 rounded-md px-2 text-sm">
                <option value="interna">Diffida Interna</option>
                <option value="esterna">Diffida Esterna</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border-2 border-slate-200">
            <table className="min-w-full text-xs" data-testid="massiva-preview-table">
              <thead className="bg-slate-100 sticky top-0">
                <tr className="text-left text-slate-700">
                  <th className="px-2 py-2">Riga</th>
                  <th className="px-2 py-2">Stato</th>
                  <th className="px-2 py-2">Nome debitore</th>
                  <th className="px-2 py-2">CF</th>
                  <th className="px-2 py-2">Indirizzo</th>
                  <th className="px-2 py-2 text-right">Totale insoluto</th>
                  <th className="px-2 py-2">PEC / Email</th>
                  <th className="px-2 py-2 text-right">Rate</th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r) => (
                  <tr key={r._row} data-testid={`massiva-riga-${r._row}`}
                      className={r._valid ? "border-t border-slate-100 hover:bg-slate-50" : "border-t border-red-100 bg-red-50/40"}>
                    <td className="px-2 py-1.5 font-mono text-slate-500">{r._row}</td>
                    <td className="px-2 py-1.5">
                      {r._valid
                        ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> OK</span>
                        : <span className="text-red-700" title={r._warnings?.join(" · ")}>{r._warnings?.[0] || "Errore"}</span>}
                    </td>
                    <td className="px-2 py-1.5 font-semibold text-slate-800">{r.nome || "—"}</td>
                    <td className="px-2 py-1.5 font-mono uppercase text-slate-600">{r.codice_fiscale || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-600 truncate max-w-[220px]">
                      {[r.via, r.civico].filter(Boolean).join(" ") || "—"}
                      {r.citta && <div className="text-[10px] text-slate-400">{r.cap} {r.citta} {r.provincia && `(${r.provincia})`}</div>}
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold">
                      {typeof r.totale_insoluto === "number"
                        ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(r.totale_insoluto)
                        : (r.totale_insoluto || "—")}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600 truncate max-w-[180px]">
                      {r.pec || r.email || "—"}
                    </td>
                    <td className="px-2 py-1.5 text-right">{r.numero_rate || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {invalidi > 0 && (
            <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2">
              <b>Attenzione:</b> {invalidi} riga/righe presentano dati incompleti (evidenziate in rosso). Verranno <b>escluse</b> dalla generazione.
              Correggi il file Excel e ricaricalo, oppure procedi solo con le {validi} righe valide.
            </div>
          )}
        </div>

        <div className="border-t-2 border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between gap-2">
          <div className="text-sm text-slate-700">
            Verranno generate <b className="text-brand">{validi}</b> diffide in <b>bozza</b> · modello: <b>{tipo === "interna" ? "Diffida Interna" : "Diffida Esterna"}</b>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Annulla</Button>
            <Button size="sm" onClick={onConfirm} disabled={busy || validi === 0}
              data-testid="massiva-conferma"
              className="bg-brand hover:bg-brand-dark text-white">
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generazione…</> : <><Send className="w-4 h-4 mr-1" /> Genera {validi} diffide</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


function ParamsEditor({ d, onSave, disabled }) {
  const [p, setP] = useState({
    canale_invio: d.params?.canale_invio || (d.debitore?.pec ? "PEC" : "Raccomandata A/R"),
    importo_spese: d.params?.importo_spese ?? 0,
    importo_competenze_legali: d.params?.importo_competenze_legali ?? 0,
    importo_interessi: d.params?.importo_interessi ?? 0,
    iban_creditore: d.params?.iban_creditore || "",
  });
  const totale =
    (Number(d.debitore?.totale_insoluto) || 0) +
    (Number(p.importo_spese) || 0) +
    (Number(p.importo_competenze_legali) || 0) +
    (Number(p.importo_interessi) || 0);
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2">
      <div className="text-[11px] font-bold uppercase text-slate-500">Parametri diffida</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <div>
          <Label className="text-[10px] uppercase">Canale invio</Label>
          <select value={p.canale_invio} onChange={(e) => setP({ ...p, canale_invio: e.target.value })}
            data-testid={`diffida-canale-${d.id}`}
            className="mt-1 h-9 w-full border-2 border-slate-300 rounded-md px-2 text-sm">
            <option>PEC</option><option>Raccomandata A/R</option>
          </select>
        </div>
        <div>
          <Label className="text-[10px] uppercase">Spese (€)</Label>
          <Input type="number" step="0.01" value={p.importo_spese}
            data-testid={`diffida-spese-${d.id}`}
            onChange={(e) => setP({ ...p, importo_spese: e.target.value })}
            className="mt-1 h-9 text-sm" />
        </div>
        <div>
          <Label className="text-[10px] uppercase">Competenze legali (€)</Label>
          <Input type="number" step="0.01" value={p.importo_competenze_legali}
            data-testid={`diffida-competenze-${d.id}`}
            onChange={(e) => setP({ ...p, importo_competenze_legali: e.target.value })}
            className="mt-1 h-9 text-sm" />
        </div>
        <div>
          <Label className="text-[10px] uppercase">Interessi (€)</Label>
          <Input type="number" step="0.01" value={p.importo_interessi}
            data-testid={`diffida-interessi-${d.id}`}
            onChange={(e) => setP({ ...p, importo_interessi: e.target.value })}
            className="mt-1 h-9 text-sm" />
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <Label className="text-[10px] uppercase">IBAN Creditore (per bonifico)</Label>
          <Input value={p.iban_creditore}
            data-testid={`diffida-iban-${d.id}`}
            onChange={(e) => setP({ ...p, iban_creditore: e.target.value })}
            placeholder="IT60 X054 2811 1010 0000 0123 456" className="mt-1 h-9 text-sm font-mono" />
        </div>
        <div className="self-end">
          <div className="text-[10px] uppercase text-emerald-800 font-bold">Totale complessivo</div>
          <div className="font-bold text-emerald-800 text-lg">{fmtEUR(totale)}</div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={() => onSave({
          ...p,
          importo_spese: Number(p.importo_spese) || 0,
          importo_competenze_legali: Number(p.importo_competenze_legali) || 0,
          importo_interessi: Number(p.importo_interessi) || 0,
        })} disabled={disabled}
          data-testid={`diffida-save-params-${d.id}`}
          className="bg-emerald-700 hover:bg-emerald-800">
          <Pencil className="w-3.5 h-3.5 mr-1" /> Salva parametri
        </Button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Iter122k140 · Metric card usata nella dashboard tracking
// ─────────────────────────────────────────────────────────────────────────
function MetricCard({ label, value, highlight, isText, testid }) {
  const clr = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    slate: "bg-slate-50 border-slate-200 text-slate-800",
    brand: "bg-brand/10 border-brand/40 text-brand-dark",
  }[highlight] || "bg-white border-slate-200 text-slate-900";
  return (
    <div data-testid={testid}
      className={`rounded-xl border-2 p-2.5 ${clr}`}>
      <div className="text-[10px] uppercase font-bold opacity-80">{label}</div>
      <div className={`font-bold ${isText ? "text-lg" : "text-2xl"} leading-tight`}>{value}</div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Iter122k140 · Box delle ricevute (accettazione/consegna/spedizione)
// ─────────────────────────────────────────────────────────────────────────
function RicevuteBox({ d, onUpload, onDownload, onDelete, disabled }) {
  const [tipo, setTipo] = useState("accettazione");
  const [note, setNote] = useState("");
  const inputRef = useRef(null);
  const ricevute = d.ricevute || [];
  return (
    <div className="rounded-lg bg-white border-2 border-slate-200 p-3 space-y-2"
         data-testid={`diffida-ricevute-${d.id}`}>
      <div className="text-[11px] font-bold uppercase text-slate-500 flex items-center gap-1">
        <Paperclip className="w-3 h-3" /> Ricevute ({ricevute.length})
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-[10px] uppercase">Tipo</Label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}
            data-testid={`diffida-ricevuta-tipo-${d.id}`}
            className="mt-1 h-9 border-2 border-slate-300 rounded-md px-2 text-sm">
            <option value="accettazione">Accettazione PEC</option>
            <option value="consegna">Consegna PEC</option>
            <option value="spedizione">Distinta spedizione</option>
            <option value="mancata_consegna">Mancata consegna</option>
            <option value="altro">Altro</option>
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label className="text-[10px] uppercase">Nota (opz.)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)}
            data-testid={`diffida-ricevuta-nota-${d.id}`}
            placeholder="Es. Ricevuta di consegna del 12/03" className="mt-1 h-9 text-sm" />
        </div>
        <label className="cursor-pointer">
          <input ref={inputRef} type="file"
            data-testid={`diffida-ricevuta-file-${d.id}`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) { onUpload(tipo, f, note); setNote(""); if (inputRef.current) inputRef.current.value = ""; }
            }} className="hidden" disabled={disabled} />
          <span className="inline-flex items-center gap-1 h-9 px-3 rounded-md border-2 border-brand text-brand hover:bg-brand/10 text-sm font-semibold">
            <Upload className="w-4 h-4" /> Carica
          </span>
        </label>
      </div>
      {ricevute.length > 0 && (
        <div className="space-y-1">
          {ricevute.map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-xs">
              <span className="font-bold text-slate-700">{r.tipo}</span>
              <span className="text-slate-500 truncate flex-1">{r.nome_originale}</span>
              <button type="button" onClick={() => onDownload(r)}
                data-testid={`diffida-ricevuta-dl-${r.id}`}
                className="text-brand hover:text-brand-dark" title="Scarica">
                <Download className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => onDelete(r.id)}
                data-testid={`diffida-ricevuta-del-${r.id}`}
                className="text-red-600 hover:text-red-800" title="Rimuovi">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Iter122k140 · Modal registrazione invio
// ─────────────────────────────────────────────────────────────────────────
function RegistraInvioModal({ diffida, documenti, onClose, onSubmit }) {
  const suggerito = diffida.debitore?.pec ? "PEC" : "Raccomandata A/R";
  const [payload, setPayload] = useState({
    data: new Date().toISOString().slice(0, 10),
    mezzo: suggerito, costo: "", note: "", allegati_ids: [],
  });
  // Suggerisci allegati per tipo pratica: prospetto rate (interna) o fatture (esterna)
  const relevanti = (documenti || []).filter((doc) => {
    const nome = (doc.nome_originale || "").toLowerCase();
    if (diffida.tipo === "interna") {
      return nome.includes("prospetto") || nome.includes("rate") || nome.includes("insolut");
    }
    return nome.includes("fattur") || nome.includes(".pdf");
  });
  const toggle = (id) => setPayload((p) => ({
    ...p,
    allegati_ids: p.allegati_ids.includes(id)
      ? p.allegati_ids.filter((x) => x !== id)
      : [...p.allegati_ids, id],
  }));
  const submit = () => {
    if (!payload.data) return toast.error("Data invio obbligatoria");
    onSubmit({
      ...payload, costo: Number(payload.costo || 0),
    });
  };
  const isPostaOnline = payload.mezzo === "Posta online";
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
         data-testid="diffida-invio-modal">
      <div className="bg-white w-full max-w-lg rounded-xl border-2 border-slate-200 shadow-xl">
        <div className="flex items-center justify-between gap-2 p-4 border-b-2 border-slate-100">
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900">Registra invio</h3>
            <p className="text-xs text-slate-500">{diffida.debitore?.nome}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase">Data invio *</Label>
              <Input type="date" value={payload.data}
                data-testid="diffida-invio-data"
                onChange={(e) => setPayload({ ...payload, data: e.target.value })}
                className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase">Mezzo *</Label>
              <select value={payload.mezzo}
                data-testid="diffida-invio-mezzo"
                onChange={(e) => setPayload({ ...payload, mezzo: e.target.value })}
                className="mt-1 h-10 w-full border-2 border-slate-300 rounded-md px-2 text-sm">
                <option value="PEC">PEC</option>
                <option value="Raccomandata A/R">Raccomandata A/R</option>
                <option value="Consegna a mano">Consegna a mano</option>
                <option value="Posta online" disabled>Posta online (prossima integrazione)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase">Costo (€, opz.)</Label>
              <Input type="number" step="0.01" value={payload.costo}
                data-testid="diffida-invio-costo"
                onChange={(e) => setPayload({ ...payload, costo: e.target.value })}
                className="mt-1" placeholder="0.00" />
            </div>
            <div>
              <Label className="text-xs uppercase">Note (opz.)</Label>
              <Input value={payload.note}
                data-testid="diffida-invio-note"
                onChange={(e) => setPayload({ ...payload, note: e.target.value })}
                placeholder="Numero racc. / protocollo / ecc." className="mt-1" />
            </div>
          </div>
          {relevanti.length > 0 && (
            <div>
              <Label className="text-xs uppercase">Allegati suggeriti</Label>
              <div className="mt-1 space-y-1 max-h-40 overflow-auto rounded-md border border-slate-200 p-2">
                {relevanti.map((doc) => (
                  <label key={doc.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                    <input type="checkbox" checked={payload.allegati_ids.includes(doc.id)}
                      onChange={() => toggle(doc.id)}
                      data-testid={`diffida-invio-allegato-${doc.id}`} />
                    <Paperclip className="w-3 h-3 text-slate-400" />
                    <span className="truncate">{doc.nome_originale}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {isPostaOnline && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
              Integrazione Posta Power / postalizzazione online in arrivo. Usa un altro mezzo per ora.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t-2 border-slate-100 bg-slate-50 rounded-b-xl">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} disabled={isPostaOnline}
            data-testid="diffida-invio-submit"
            className="bg-emerald-700 hover:bg-emerald-800">
            <Send className="w-4 h-4 mr-1" /> Registra invio
          </Button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Iter122k141 · Fase 3 · Riepilogo PDR inline sulla diffida
// ─────────────────────────────────────────────────────────────────────────
function PdrSummaryBox({ d, pid, onReload, disabled }) {
  const pdr = d.pdr || {};
  const [showRate, setShowRate] = useState(false);
  const badgeColor = {
    bozza: "bg-slate-100 border-slate-300 text-slate-700",
    proposto: "bg-amber-50 border-amber-300 text-amber-800",
    firmato: "bg-emerald-50 border-emerald-300 text-emerald-800",
    annullato: "bg-red-50 border-red-300 text-red-800",
  }[pdr.stato] || "bg-slate-100 border-slate-300 text-slate-700";
  const invitaFirma = async () => {
    try {
      const { data } = await api.post(`/credius/${pid}/diffide/${d.id}/pdr/invita-firma`);
      toast.success(`Invito inviato a ${data.email}`);
      onReload();
    } catch (e) { toast.error(formatApiError(e) || "Invio fallito"); }
  };
  const annulla = async () => {
    const note = window.prompt("Motivo annullamento (visibile in cronologia):", "");
    if (note === null) return;
    try {
      await api.post(`/credius/${pid}/diffide/${d.id}/pdr/annulla`, { note });
      toast.success("PDR annullato · passaggio a giudiziale");
      onReload();
    } catch (e) { toast.error(formatApiError(e) || "Annullamento fallito"); }
  };
  const scaricaPdrPdf = async () => {
    try {
      const r = await api.get(`/credius/${pid}/diffide/${d.id}/pdr/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatApiError(e) || "PDF non disponibile"); }
  };
  // Iter122k142 · Fase 4 · incasso/annulla incasso rata
  const incassa = async (n) => {
    try {
      await api.post(`/credius/${pid}/diffide/${d.id}/pdr/rate/${n}/incasso`, {});
      toast.success(`Rata ${n} incassata`); onReload();
    } catch (e) { toast.error(formatApiError(e) || "Registrazione fallita"); }
  };
  const annullaIncasso = async (n) => {
    if (!window.confirm(`Annullare l'incasso della rata ${n}?`)) return;
    try {
      await api.delete(`/credius/${pid}/diffide/${d.id}/pdr/rate/${n}/incasso`);
      toast.success("Incasso annullato"); onReload();
    } catch (e) { toast.error(formatApiError(e) || "Operazione fallita"); }
  };
  const rate = pdr.rate || [];
  const n_incassate = rate.filter((r) => r.incassato).length;
  const progresso = rate.length ? Math.round((n_incassate / rate.length) * 100) : 0;
  const oggi = new Date().toISOString().slice(0, 10);
  return (
    <div className="rounded-lg bg-indigo-50/60 border-2 border-indigo-200 p-3 space-y-2"
         data-testid={`diffida-pdr-summary-${d.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Handshake className="w-4 h-4 text-indigo-700" />
        <span className="font-bold text-slate-900">Piano di Rientro</span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badgeColor}`}>
          {pdr.stato}
        </span>
        <span className="text-xs text-slate-600 ml-auto">
          <b>{pdr.numero_rate}</b> rate · totale <b>{fmtEUR(pdr.importo_totale)}</b>
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 text-[11px] text-slate-600">
        <div><b>Decorrenza:</b> {pdr.decorrenza}</div>
        <div><b>Capitale:</b> {fmtEUR(pdr.importo_capitale)}</div>
        <div><b>Spese:</b> {fmtEUR(pdr.importo_spese)}</div>
        <div><b>Interessi:</b> {fmtEUR(pdr.importo_interessi)}</div>
      </div>
      {pdr.firma && (
        <div className="text-[11px] text-emerald-800">
          <Shield className="w-3 h-3 inline mr-0.5" /> Firmato da <b>{pdr.firma.signer_name}</b>
          {pdr.firma.firmato_at && ` · ${pdr.firma.firmato_at.slice(0, 16).replace('T', ' ')}`}
        </div>
      )}

      {/* Iter122k142 · Progresso + elenco rate con incasso/annulla */}
      {rate.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs">
            <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all"
                style={{ width: `${progresso}%` }} />
            </div>
            <span className="font-bold text-emerald-800 min-w-[46px] text-right">{progresso}%</span>
            <button type="button" onClick={() => setShowRate((v) => !v)}
              data-testid={`pdr-rate-toggle-${d.id}`}
              className="text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-0.5">
              {n_incassate}/{rate.length} rate {showRate ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
          {showRate && (
            <div className="rounded-md border border-slate-200 bg-white max-h-56 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 sticky top-0">
                  <tr>
                    <th className="text-left p-1.5 w-8">#</th>
                    <th className="text-left p-1.5">Scadenza</th>
                    <th className="text-right p-1.5">Importo</th>
                    <th className="text-center p-1.5">Stato</th>
                    <th className="text-right p-1.5">Azione</th>
                  </tr>
                </thead>
                <tbody>
                  {rate.map((r) => {
                    const scaduta = !r.incassato && r.data < oggi;
                    const imminente = !r.incassato && r.data >= oggi && r.data <= new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
                    return (
                      <tr key={r.n} className="border-t border-slate-100">
                        <td className="p-1.5 font-mono">{r.n}</td>
                        <td className="p-1.5">{r.data}</td>
                        <td className="p-1.5 text-right font-bold">{fmtEUR(r.importo)}</td>
                        <td className="p-1.5 text-center">
                          {r.incassato ? (
                            <span className="text-[10px] font-bold text-emerald-700 inline-flex items-center gap-0.5">
                              <CheckCircle2 className="w-3 h-3" /> Incassata
                            </span>
                          ) : scaduta ? (
                            <span className="text-[10px] font-bold text-red-700 px-1.5 py-0.5 rounded-full bg-red-50 border border-red-300">Scaduta</span>
                          ) : imminente ? (
                            <span className="text-[10px] font-bold text-amber-700 px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-300">In scadenza</span>
                          ) : (
                            <span className="text-[10px] text-slate-500">Attesa</span>
                          )}
                        </td>
                        <td className="p-1.5 text-right">
                          {pdr.stato === "firmato" && (r.incassato ? (
                            <button type="button" onClick={() => annullaIncasso(r.n)}
                              data-testid={`pdr-rata-annulla-${d.id}-${r.n}`}
                              className="text-[10px] text-red-600 hover:text-red-800">Annulla</button>
                          ) : (
                            <button type="button" onClick={() => incassa(r.n)}
                              data-testid={`pdr-rata-incassa-${d.id}-${r.n}`}
                              className="text-[10px] font-bold text-emerald-700 hover:text-emerald-900">✓ Incasso</button>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        <Button size="sm" variant="outline" onClick={scaricaPdrPdf}
          data-testid={`diffida-pdr-pdf-${d.id}`}
          className="h-8 border-indigo-300 text-indigo-800 hover:bg-indigo-100">
          <FileText className="w-3.5 h-3.5 mr-1" /> PDF PDR
        </Button>
        {pdr.stato === "bozza" && (
          <Button size="sm" onClick={invitaFirma} disabled={disabled}
            data-testid={`diffida-pdr-invita-${d.id}`}
            className="h-8 bg-amber-600 hover:bg-amber-700">
            <Mail className="w-3.5 h-3.5 mr-1" /> Invita a firmare
          </Button>
        )}
        {pdr.stato !== "annullato" && (
          <Button size="sm" variant="outline" onClick={annulla} disabled={disabled}
            data-testid={`diffida-pdr-annulla-${d.id}`}
            className="h-8 border-red-300 text-red-700 hover:bg-red-50">
            <X className="w-3.5 h-3.5 mr-1" /> Annulla PDR
          </Button>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Iter122k141 · Fase 3 · Wizard di creazione/modifica PDR
// ─────────────────────────────────────────────────────────────────────────
function PdrWizardModal({ pid, diffida, onClose, onSaved }) {
  const prev = diffida.pdr || {};
  const [form, setForm] = useState({
    numero_rate: prev.numero_rate || 6,
    decorrenza: prev.decorrenza || new Date().toISOString().slice(0, 10),
    importo_capitale: prev.importo_capitale ?? (diffida.debitore?.totale_insoluto || 0),
    importo_spese: prev.importo_spese ?? (diffida.params?.importo_spese || 0),
    importo_competenze_legali: prev.importo_competenze_legali ?? (diffida.params?.importo_competenze_legali || 0),
    importo_interessi: prev.importo_interessi ?? (diffida.params?.importo_interessi || 0),
    iban_creditore: prev.iban_creditore || diffida.params?.iban_creditore || "",
  });
  const [rate, setRate] = useState(prev.rate || []);
  const [busy, setBusy] = useState(false);
  const totale = round2(
    Number(form.importo_capitale) + Number(form.importo_spese) +
    Number(form.importo_competenze_legali) + Number(form.importo_interessi)
  );
  const preview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/credius/${pid}/diffide/${diffida.id}/pdr/preview`,
        { numero_rate: Number(form.numero_rate), decorrenza: form.decorrenza, importo_totale: totale });
      setRate(data.rate);
    } catch (e) { toast.error(formatApiError(e) || "Calcolo fallito"); }
    finally { setBusy(false); }
  };
  const salva = async () => {
    setBusy(true);
    try {
      await api.put(`/credius/${pid}/diffide/${diffida.id}/pdr`, {
        numero_rate: Number(form.numero_rate),
        decorrenza: form.decorrenza,
        importo_capitale: Number(form.importo_capitale),
        importo_spese: Number(form.importo_spese),
        importo_competenze_legali: Number(form.importo_competenze_legali),
        importo_interessi: Number(form.importo_interessi),
        iban_creditore: form.iban_creditore,
      });
      toast.success("PDR salvato");
      onSaved();
    } catch (e) { toast.error(formatApiError(e) || "Salvataggio fallito"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-auto"
         data-testid="diffida-pdr-modal">
      <div className="bg-white w-full max-w-2xl rounded-xl border-2 border-slate-200 shadow-xl">
        <div className="flex items-center justify-between gap-2 p-4 border-b-2 border-slate-100">
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900">
              Piano di Rientro · {diffida.debitore?.nome}
            </h3>
            <p className="text-xs text-slate-500">
              Configura numero rate, decorrenza e costi. Le scadenze successive
              seguiranno l'ultimo giorno del mese ove necessario.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase">Numero rate (max 48) *</Label>
              <select value={form.numero_rate}
                data-testid="pdr-numero-rate"
                onChange={(e) => setForm({ ...form, numero_rate: e.target.value })}
                className="mt-1 h-10 w-full border-2 border-slate-300 rounded-md px-2 text-sm">
                {Array.from({ length: 48 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase">Decorrenza prima rata *</Label>
              <Input type="date" value={form.decorrenza}
                data-testid="pdr-decorrenza"
                onChange={(e) => setForm({ ...form, decorrenza: e.target.value })}
                className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase">Capitale (€)</Label>
              <Input type="number" step="0.01" value={form.importo_capitale}
                data-testid="pdr-capitale"
                onChange={(e) => setForm({ ...form, importo_capitale: e.target.value })}
                className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase">Spese generali (€)</Label>
              <Input type="number" step="0.01" value={form.importo_spese}
                data-testid="pdr-spese"
                onChange={(e) => setForm({ ...form, importo_spese: e.target.value })}
                className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase">Compenso legale (€)</Label>
              <Input type="number" step="0.01" value={form.importo_competenze_legali}
                data-testid="pdr-competenze"
                onChange={(e) => setForm({ ...form, importo_competenze_legali: e.target.value })}
                className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase">Interessi (€)</Label>
              <Input type="number" step="0.01" value={form.importo_interessi}
                data-testid="pdr-interessi"
                onChange={(e) => setForm({ ...form, importo_interessi: e.target.value })}
                className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase">IBAN creditore</Label>
              <Input value={form.iban_creditore}
                data-testid="pdr-iban"
                onChange={(e) => setForm({ ...form, iban_creditore: e.target.value })}
                placeholder="IT60 X054 2811 1010 0000 0123 456"
                className="mt-1 font-mono text-sm" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-indigo-50 border border-indigo-200 p-2">
            <div className="text-sm">
              <span className="text-slate-600">Totale complessivo:</span>{" "}
              <b className="text-indigo-900">{fmtEUR(totale)}</b>
            </div>
            <Button size="sm" variant="outline" onClick={preview} disabled={busy}
              data-testid="pdr-preview"
              className="border-indigo-300 text-indigo-800 hover:bg-indigo-100">
              <Calendar className="w-3.5 h-3.5 mr-1" /> Ricalcola rate
            </Button>
          </div>
          {rate.length > 0 && (
            <div className="rounded-lg border-2 border-slate-200 max-h-56 overflow-auto"
                 data-testid="pdr-rate-preview">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-700 sticky top-0">
                  <tr>
                    <th className="text-left p-1.5">#</th>
                    <th className="text-left p-1.5">Scadenza</th>
                    <th className="text-right p-1.5">Importo</th>
                  </tr>
                </thead>
                <tbody>
                  {rate.map((r) => (
                    <tr key={r.n} className="border-t border-slate-100">
                      <td className="p-1.5 font-mono">{r.n}</td>
                      <td className="p-1.5">{r.data}</td>
                      <td className="p-1.5 text-right font-bold">{fmtEUR(r.importo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t-2 border-slate-100 bg-slate-50 rounded-b-xl">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={salva} disabled={busy}
            data-testid="pdr-save"
            className="bg-indigo-700 hover:bg-indigo-800">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Handshake className="w-4 h-4 mr-1" />}
            Salva PDR
          </Button>
        </div>
      </div>
    </div>
  );
}

function round2(n) { return Math.round((n || 0) * 100) / 100; }
